<script setup lang="ts">
import { AfirData } from '@/types/statistics'
import { useMapStore } from '~/stores/map'
import AppLoadingWrapper from '~/components/app/AppLoadingWrapper.vue'
import CardStatWrapper from '~/components/layout/CardStatWrapper.vue'
import { HeatMapSettings, HeatmapData, MapAreaTypes } from '~/types/map'
import AfirDataTable from '~/components/data-views/AfirDataTable.vue'
import {createSlug} from '~/stores/utils/filter-util'

const { formattedNumber } = useFormattedNumber()

const mapStore = useMapStore()
mapStore.resetHeatmapSettings()

onUnmounted(() => {
  mapStore.resetHeatmapSettings()
})
const selectedCountyId = ref<string | null>(null)
const { data: afirDataMunicipality } = useAfirData('municipality')
const afirDataMunicipalityFiltered = computed(() => {
  if (!afirDataMunicipality.value) return []
  return afirDataMunicipality.value.filter((data) => {
    return areaType.value === 'municipality' && selectedCountyId.value
      ? data.countyCode === selectedCountyId.value
      : true
  })
})
const { data: afirDataCounty } = useAfirData('county')
const { data: afirDataCountry } = useAfirData(undefined)

const totalPower = computed(() => {
  if (!afirDataCountry.value) return 0
  return Math.round(afirDataCountry.value[0].totalPower / 1000)
})

const requiredPowerPluginHybridVehicle = computed(() => {
  if (!afirDataCountry.value) return 0
  return Math.round(afirDataCountry.value[0].requiredPowerPluginHybridVehicle / 1000)
})
const requiredPowerElectricVehicle = computed(() => {
  if (!afirDataCountry.value) return 0
  return Math.round(afirDataCountry.value[0].requiredPowerElectricVehicle / 1000)
})
const totalElectricVehicleCount = computed(() => {
  if (!afirDataCountry.value) return 0
  return afirDataCountry.value[0].totalElectricVehicleCount
})
const totalPluginHybridVehicleCount = computed(() => {
  if (!afirDataCountry.value) return 0
  return afirDataCountry.value[0].totalPluginHybridVehicleCount
})

const areaType = ref<MapAreaTypes>('municipality')
watch(areaType, (newValue) => {
  if (!newValue) return
  updateHeatmapSettings()
})

watch(
  afirDataCounty,
  (newValue) => {
    if (!newValue) return
    const parsedData = parseHeatmapdata(newValue)
    mapStore.setHeatmapCountyData(parsedData)
  },
  { immediate: true }
)

watch(
  afirDataMunicipalityFiltered,
  (newValue) => {
    if (!newValue) return
    const parsedData = parseHeatmapdata(newValue)
    mapStore.setHeatmapMunicipalityData(parsedData)
  },
  { immediate: true }
)

const { keyValueOptions: areaTypeOptions } = await useKeyTranslator('areaType')

function parseHeatmapdata(datapackage: AfirData[]) {
  const parsedData: HeatmapData[] = datapackage.map((afirData) => ({
    id: afirData.regionId,
    value: afirData.isAfirCompliant ? 1 : 0,
    displayValue: parseDisplayValue(afirData),
  }))
  return parsedData
}
function parseDisplayValue(datapackage: AfirData) {
  const difference =
    datapackage.totalPower -
    datapackage.requiredPowerElectricVehicle -
    datapackage.requiredPowerPluginHybridVehicle
  const prefix = difference > 0 ? 'Kapacitet över AFIR:' : 'Kapacitet under AFIR:'
  const formattedValue = `${prefix} ${(difference / 1000).toPrecision(3)} MW`
  return formattedValue
}

function updateHeatmapSettings() {
  mapStore.setHeatmapSettings({
    dataSuffix: '',
    areaType: areaType.value,
    legendColorSteps: 2,
    legendStepLabels: { 0: 'Under AFIR', 1: 'Över AFIR' },
    customValueRange: [0, 1],
    colors: { from: '#E69138', to: '#074D65' },
  } as HeatMapSettings)
}
updateHeatmapSettings()
</script>
<template>
  <fieldset class="mb-5">
    <legend class="flex align-items-center px-2 pb-2">
      Välj vilka områden som ska visualiseras på kartan
    </legend>
    <div class="flex">
      <div v-for="option in areaTypeOptions" :key="option.value" class="flex ml-2">
        <RadioButton
          v-model="areaType"
          :value="option.value"
          :input-id="createSlug(option.value)"
        />
        <label class="ml-2" :for="createSlug(option.value)">{{ option.label }}</label>
      </div>
    </div>
  </fieldset>
  <div v-if="areaType === 'municipality'">
    <AppHeading title="Visa endast kommuner inom valt län" type="h3" size="large" />
    <FormCountySelect v-model="selectedCountyId" />
  </div>
  <div class="my-4">
    <div>
      <AppLoadingWrapper :is-loading="!afirDataCountry" :height="100">
        <AppHeading title="Laddbara fordon i Sverige" type="h3" size="large" />
        <CardStatWrapper>
          <CardStat
            label="Antal laddhybrider"
            :value="`${formattedNumber(totalPluginHybridVehicleCount)} st`"
          />
          <CardStat
            label="Antal elfordon"
            :value="`${formattedNumber(totalElectricVehicleCount)} st`"
          />
        </CardStatWrapper>
        <AppHeading title="Nationella effektkrav AFIR" type="h3" size="large" />
        <CardStatWrapper>
          <CardStat
            label="AFIR laddhybrider"
            :value="`${formattedNumber(requiredPowerPluginHybridVehicle)} MW`"
          />
          <CardStat
            label="AFIR elfordon"
            :value="`${formattedNumber(requiredPowerElectricVehicle)} MW`"
          />
          <CardStat
            label="AFIR totalt"
            :value="`${formattedNumber(
              requiredPowerPluginHybridVehicle + requiredPowerElectricVehicle
            )} MW`"
          />
        </CardStatWrapper>
        <AppHeading title="Nationell laddeffekt" type="h3" size="large" />
        <CardStatWrapper>
          <CardStat label="Total effekt" :value="`${formattedNumber(totalPower)} MW`" />
        </CardStatWrapper>
      </AppLoadingWrapper>
    </div>
    <AppLoadingWrapper
      v-if="areaType === 'municipality'"
      :is-loading="!afirDataMunicipality"
      :height="100"
    >
      <div class="mb-3 mt-4">
        <AppHeading title="Översikt" type="h3" size="large" />
      </div>

      <AfirDataTable :afir-data="afirDataMunicipalityFiltered" :area-type="'municipality'" />
    </AppLoadingWrapper>
    <AppLoadingWrapper v-if="areaType === 'county'" :is-loading="!afirDataCounty" :height="100">
      <AfirDataTable :afir-data="afirDataCounty" :area-type="'county'" />
    </AppLoadingWrapper>
    <DataSource :source-data="[{ identifier: 'nobil' }, { identifier: 'trafa' }]" />
  </div>
</template>
